/**
 * Dispatch reads and writes dates as Cairo wall-clock time without a zone suffix
 * (e.g. "2026-09-16T00:00:00"). Sending UTC shifts every day boundary, so all date
 * parameters go through these helpers.
 */
import type { Locale } from '@/stores/ui'

const CAIRO = 'Africa/Cairo'

/** Wall-clock parts of an instant in Cairo. */
function cairoParts(date: Date) {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: CAIRO, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23',
  }).formatToParts(date)
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '00'
  return { y: get('year'), m: get('month'), d: get('day'), h: get('hour'), min: get('minute'), s: get('second') }
}

/** "YYYY-MM-DD" for the Cairo calendar day of `date`. */
export function cairoDay(date: Date = new Date()): string {
  const p = cairoParts(date)
  return `${p.y}-${p.m}-${p.d}`
}

/** Start of a Cairo day as the zone-less string Dispatch expects. */
export const dayStart = (day: string) => `${day}T00:00:00`
/** End of a Cairo day (inclusive). */
export const dayEnd = (day: string) => `${day}T23:59:59`

/** Shift a YYYY-MM-DD string by whole days (calendar arithmetic, zone independent). */
export function addDays(day: string, n: number): string {
  const [y, m, d] = day.split('-').map(Number)
  const dt = new Date(Date.UTC(y!, m! - 1, d! + n))
  return dt.toISOString().slice(0, 10)
}

export interface DateRange {
  from: string
  to: string
}

export type RangePreset = 'today' | 'yesterday' | 'last7' | 'last30' | 'last90' | 'last3Months' | 'thisMonth'

export function presetRange(preset: RangePreset, now = new Date()): DateRange {
  const today = cairoDay(now)
  switch (preset) {
    case 'today':
      return { from: today, to: today }
    case 'yesterday':
      return { from: addDays(today, -1), to: addDays(today, -1) }
    case 'last7':
      return { from: addDays(today, -6), to: today }
    case 'last30':
      return { from: addDays(today, -29), to: today }
    case 'last90':
      return { from: addDays(today, -89), to: today }
    case 'last3Months': {
      // the same day three calendar months back (31 Jan → 31 Oct), clamped to that month's last day
      const [y, m, d] = today.split('-').map(Number)
      const back = new Date(Date.UTC(y, m - 1 - 3, 1))
      const last = new Date(Date.UTC(back.getUTCFullYear(), back.getUTCMonth() + 1, 0)).getUTCDate()
      back.setUTCDate(Math.min(d, last))
      return { from: back.toISOString().slice(0, 10), to: today }
    }
    case 'thisMonth':
      return { from: `${today.slice(0, 8)}01`, to: today }
  }
}

/** Range → Dispatch `fromDate` / `toDate` pair. */
export const toDispatchRange = (r: DateRange) => ({ fromDate: dayStart(r.from), toDate: dayEnd(r.to) })

/**
 * Parses a Dispatch timestamp. Zone-less values are Cairo wall time; values with Z/offset are absolute.
 * Returns null for the .NET "0001-01-01" sentinel.
 */
export function parseDispatchDate(value: string | number | Date | null | undefined): Date | null {
  /*
   * …and a plain epoch number, because our own tables keep their times that way. A `datetime` column whose accessor
   * returned a number used to reach `value.startsWith`, throw, and take the whole screen down with "حصل خطأ غير
   * متوقع في الصفحة دي" — متابعة رسايل الاستلام and طلبات تغيير الحالة المعلقة both died on their own time column
   * (owner, 2026-09-27). A time is a time whichever way it is written.
   */
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value
  if (typeof value === 'number') {
    if (!Number.isFinite(value) || value <= 0) return null
    // seconds or milliseconds: below this bound (1973 in ms) nothing anybody stores is a real date
    const d = new Date(value < 100_000_000_000 ? value * 1000 : value)
    return Number.isNaN(d.getTime()) ? null : d
  }
  if (!value || value.startsWith('0001-01-01')) return null
  if (/[zZ]|[+-]\d\d:?\d\d$/.test(value)) {
    const d = new Date(value)
    return Number.isNaN(d.getTime()) ? null : d
  }
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?)?/.exec(value)
  if (!m) return null
  const [, y, mo, d, h = '00', mi = '00', s = '00'] = m
  const asUtc = Date.UTC(+y!, +mo! - 1, +d!, +h, +mi, +s)
  // Find Cairo's offset at that moment (handles DST) and correct.
  const probe = cairoParts(new Date(asUtc))
  const cairoAsUtc = Date.UTC(+probe.y, +probe.m - 1, +probe.d, +probe.h, +probe.min, +probe.s)
  return new Date(asUtc - (cairoAsUtc - asUtc))
}

const fmtCache = new Map<string, Intl.DateTimeFormat>()
export function formatDateTime(value: string | number | Date | null | undefined, locale: Locale, withTime = true): string {
  const date = parseDispatchDate(value ?? null)
  if (!date || Number.isNaN(date.getTime())) return '—'
  const key = `${locale}|${withTime}`
  let f = fmtCache.get(key)
  if (!f) {
    f = new Intl.DateTimeFormat(locale === 'ar' ? 'ar-EG-u-nu-latn' : 'en-GB', {
      timeZone: CAIRO, year: 'numeric', month: 'short', day: '2-digit', ...(withTime ? { hour: '2-digit', minute: '2-digit' } : {}),
    })
    fmtCache.set(key, f)
  }
  return f.format(date)
}

export function relativeTime(value: string | number | Date | null | undefined, locale: Locale): string {
  const date = parseDispatchDate(value ?? null)
  if (!date || Number.isNaN(date.getTime())) return '—'
  const diff = (date.getTime() - Date.now()) / 1000
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' })
  const abs = Math.abs(diff)
  if (abs < 60) return rtf.format(Math.round(diff), 'second')
  if (abs < 3600) return rtf.format(Math.round(diff / 60), 'minute')
  if (abs < 86400) return rtf.format(Math.round(diff / 3600), 'hour')
  return rtf.format(Math.round(diff / 86400), 'day')
}
